import { useQuery } from '@apollo/client';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';

import ErrorPage from './CommonComponents/ErrorPage/ErrorPage';
import Loader from './CommonComponents/UI/Loader';
import { DOC_CHAT, DOC_COMPARE } from './Constants/commonConstants';
import { Dashboard, DashboardDocChat } from './Features/Dashboard';
import { ProductSubscription } from './Features/ProductSelection';
import ProfileDetails from './Features/SettingPage/ProfileDetails/ProfileDetails';
import SettingsPageLayout from './Features/SettingPage/SettingsPageLayout';
import Workflow from './Features/SettingPage/Workflow/Workflow';
import WorkflowForm from './Features/SettingPage/Workflow/WorkflowForm';
import TakeTourInitialization from './Features/TakeTour/Components/TakeTourInitialization/TakeTourInitialization';
import { TakeTourProvider } from './Features/TakeTour/Context/TakeTourProvider';
import Viewer from './Features/Viewer/Viewer';
import ViewerDocChat from './Features/Viewer/ViewerDocChat';
import { axiosDocChatInstance } from './lib/axiosDocChatInstance';
import { setUserData } from './Redux/login/loginActions';
import { accountsClient } from './utils/accountsClient';
import { getValidAccessToken, redirectToLogin } from './utils/authUtils';
import { useContractSubscriptions } from './utils/hooks/useContractSubscriptions';
import { GET_USER_DATA } from './utils/queries';

axiosDocChatInstance.interceptors.request.use(
  async (config) => {
    const token = await getValidAccessToken();

    if (token) {
      config.headers = {
        ...(config.headers ?? {}),
        Authorization: `Bearer ${token}`,
      };
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response.status === 401 || error.response.status === 403) {
      redirectToLogin();
      window.location = '/';
    }
    // else if (error.response.status === 500) {
    //   window.location = '*';
    // }
    return Promise.reject(error);
  }
);

const ProtectedRoute = ({ product }) => {
  const contractSubscriptions = useContractSubscriptions();

  return contractSubscriptions[product] === undefined ? <Navigate to='/products' /> : <Outlet />;
};

function App() {
  const [state, setState] = useState({
    isAuthenticated: false,
    isLoading: true,
  });

  useEffect(() => {
    const initAuth = async () => {
      const finalState = { isAuthenticated: false, isLoading: false };

      try {
        // Token in storage (only if still not authenticated)
        if (!finalState.isAuthenticated) {
          const token = await getValidAccessToken();
          if (token) {
            finalState.isAuthenticated = true;
          }
        }
        //  Redirect if unauthenticated
        if (!finalState.isAuthenticated) {
          const cleanUrl = window.location.href.split('?')[0];
          localStorage.setItem('auth_return_url', cleanUrl);
          redirectToLogin();
        }
      } catch (err) {
        console.error('Authentication check failed:', err);
      } finally {
        setState(finalState);
      }
    };

    initAuth();
  }, []);

  // useEffect(() => {
  //   if (
  //     !user_id ||
  //     !token ||
  //     (localStorage.getItem('user_id') !== undefined &&
  //       localStorage.getItem('token') !== undefined)
  //   ) {
  //     const userId = localStorage.getItem('user_id');
  //     const userToken = localStorage.getItem('token');
  //     setUserData({ user_id: userId, token: userToken });
  //   }
  // }, []);

  if (state.isLoading || !state.isAuthenticated) {
    return <Loader style={{ height: '100vh' }} />;
  }

  return (
    <BrowserRouter>
      <TakeTourProvider>
        <AppLayout>
          <Routes>
            <Route path='/' element={<NavigateToProduct />} />

            <Route element={<ProtectedRoute product='docCompare' />}>
              <Route path='/doccompare' element={<Dashboard />} />
              <Route path='/viewer' element={<Viewer />} />
            </Route>

            <Route element={<ProtectedRoute product='docChat' />}>
              <Route path='/docchat' element={<DashboardDocChat />} />
              <Route path='/viewer-docchat' element={<ViewerDocChat />} />
            </Route>
            <Route path='settings' element={<SettingsPageLayout />}>
              <Route index element={<ProfileDetails />} />
              <Route path='doccompare' element={<ProfileDetails product={DOC_COMPARE} />} />
              <Route path='docchat' element={<ProfileDetails product={DOC_CHAT} />} />
              <Route element={<ProtectedRoute product='docChat' />}>
                <Route path='docchat/workflow' element={<Workflow />} />
                <Route path='docchat/workflow/create' element={<WorkflowForm />} />
                <Route path='docchat/workflow/edit/:id' element={<WorkflowForm />} />
                {/* <Route path="playbook" element={<Playbook />} /> */}
              </Route>
            </Route>
            <Route path='*' element={<ErrorPage />} />
            <Route path='/products' element={<ProductSubscription />} />
          </Routes>
        </AppLayout>
      </TakeTourProvider>
    </BrowserRouter>
  );
}

function NavigateToProduct() {
  return <Navigate to='/products' />;
}

function AppLayout({ children }) {
  const dispatch = useDispatch();
  const { data: userData, loading } = useQuery(GET_USER_DATA, {
    client: accountsClient,
    errorPolicy: 'all',
  });

  useEffect(() => {
    if (userData?.getUserById) {
      const data = userData?.getUserById;
      const hasSubscription = data?.contractUserSetting?.userSubscriptions?.some(
        (item) => item?.service === 'DOCCHAT' || item?.service === 'DOCCOMPARE'
      );
      if (!hasSubscription) {
        redirectToLogin();
      }
      dispatch(setUserData({ userData: data, user_id: data?.id }));
      localStorage.setItem('user_id', data?.id);
    }
  }, [userData, dispatch]);

  if (loading) {
    return null;
  }
  return (
    <>
      {children}
      <TakeTourInitialization />
    </>
  );
}

export default App;
